import React from "react";
import Card from "./Card";
import { useState, useEffect } from "react";
import { apiClient } from "../../../api";
import { baseUrl } from "../../../Environments/environment.js";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { Users, Scan, Bell, Home, Star, TrendingUp, Clock, CheckCircle, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";

// Cache for storing analytics data with expiration
const analyticsCache = {};
const CACHE_EXPIRATION_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

// Helper function to check if cache is valid
const isCacheValid = (cacheEntry) => {
  if (!cacheEntry || !cacheEntry.timestamp) {
    return false;
  }
  const now = Date.now();
  const cacheAge = now - cacheEntry.timestamp;
  return cacheAge < CACHE_EXPIRATION_TIME;
};

const LoadingSkeleton = () => (
  <>
    {/* Summary Section Skeleton */}
    <div className="mt-8 relative overflow-hidden bg-gradient-to-r from-indigo-600/80 to-indigo-500/80 rounded-lg shadow-lg p-6 animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="text-center">
            <div className="h-6 w-32 bg-indigo-300/30 rounded mx-auto mb-2"></div>
            <div className="h-10 w-20 bg-indigo-300/30 rounded mx-auto mb-2"></div>
            <div className="h-4 w-40 bg-indigo-300/30 rounded mx-auto"></div>
          </div>
        ))}
      </div>
    </div>

    <hr className="border-t border-gray-200 my-8" />

    {/* Cards Skeleton */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
          <div className="flex items-center space-x-4">
            <div className="w-8 h-8 bg-indigo-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
              <div className="h-6 w-16 bg-gray-300 rounded"></div>
            </div>
          </div>
          <div className="mt-4">
            <div className="h-3 w-full bg-gray-100 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  </>
);

const Overview = () => {
  const { selectedPropertyId } = useProperty();
  
  // Initialize loading state based on cache availability
  const getInitialLoadingState = () => {
    const cacheEntry = analyticsCache[selectedPropertyId];
    return !(cacheEntry && isCacheValid(cacheEntry));
  };
  
  // Initialize analytics from cache if available
  const getInitialAnalytics = () => {
    const cacheEntry = analyticsCache[selectedPropertyId];
    if (cacheEntry && isCacheValid(cacheEntry)) {
      return cacheEntry.data;
    }
    return {
      serviceEfficiency: 0,
      totalQrCodeScan: 0,
      totalHouseKeepingRequest: 0,
      totalCompletedHouseKeepingRequest: 0,
      totalCompletedAddOnsRequest: 0,
      totalRequestCount: 0,
      revenueGeneratedByRequests: 0,
      totalReviews: 0,
      guestSatisfactionScore: 0,
      totalInHouseGuests: 0,
    };
  };
  
  const [analytics, setAnalytics] = useState(getInitialAnalytics);
  const [loading, setLoading] = useState(getInitialLoadingState);

  useEffect(() => {
    const fetchAnalytics = async () => {
      // Check if data exists in cache for this property and is still valid
      const cacheEntry = analyticsCache[selectedPropertyId];
      if (cacheEntry && isCacheValid(cacheEntry)) {
        setAnalytics(cacheEntry.data);
        setLoading(false);
        return;
      }
      
      // If cache exists but is expired, remove it
      if (cacheEntry && !isCacheValid(cacheEntry)) {
        delete analyticsCache[selectedPropertyId];
      }

      setLoading(true);
      // try {
      //   // Fetch basic analytics
      //   const response = await apiClient.get(
      //     `${baseUrl}/qrCodeScan/${selectedPropertyId}`,
      //   );
      //   console.log("QR Code Scan Response:", response.data.result);

      //   // Fetch all addon requests
      //   const addonsResponse = await apiClient.get(
      //     `${baseUrl}/guest/addons/${selectedPropertyId}?requestStatus=all`
      //   );
        
      //   // Get all requests
      //   const allRequests = addonsResponse.data.result.requests;
        
      //   // Filter regular addons and housekeeping requests
      //   const addonRequests = allRequests.filter(request => request.requestType !== "houseKeeping");
      //   const housekeepingRequests = allRequests.filter(request => request.requestType === "houseKeeping");

      //   // Calculate totals and accepted counts
      //   const totalAddons = addonRequests.length;
      //   const acceptedAddons = addonRequests.filter(request => request.requestStatus === "Accepted").length;
      //   const totalHousekeeping = housekeepingRequests.length;
      //   const acceptedHousekeeping = housekeepingRequests.filter(request => request.requestStatus === "Accepted").length;

      //   // Calculate total revenue from add-ons that are accepted
      //   const allAcceptedAddons = addonRequests.filter(request => request.requestStatus === "Accepted");
      //   const totalRevenue = allAcceptedAddons.reduce((sum, request) => sum + (request.price || 0), 0);

      //   // Fetch in-house guests
      //   const params = {
      //     currentStatus: "Reservation" // This will get all bookings
      //   };

      //   const inHouseResponse = await apiClient.get(
      //     `${baseUrl}/guest/${selectedPropertyId}`,
      //     {
      //       params: params,
      //     }
      //   );
      //   console.log("In-house Response:", inHouseResponse.data.result.guests);

      //   // Update token if new one is provided
      //   if (inHouseResponse.headers.get("Authorization")) {
      //     const newToken = inHouseResponse.headers.get("Authorization");
      //     localStorage.setItem("accessToken", newToken.split(" ")[1]);
      //   }

      //   // Filter guests with status.currentStatus === "In House"
      //   const allGuests = inHouseResponse.data.result.guests || [];
      //   const inHouseGuests = allGuests.filter(guest => guest.status?.currentStatus === "In House");
      //   const totalInHouseGuests = inHouseGuests.length;

      //   console.log("All Guests Count:", allGuests.length);
      //   console.log("In-house Guests:", totalInHouseGuests);

      //   console.log("Regular Addons:", {
      //     total: totalAddons,
      //     accepted: acceptedAddons
      //   });
      //   console.log("Housekeeping:", {
      //     total: totalHousekeeping,
      //     accepted: acceptedHousekeeping
      //   });

      //   const totalRequests = totalAddons + totalHousekeeping;
      //   const acceptedRequests = acceptedAddons + acceptedHousekeeping;
      //   const serviceEfficiency = totalRequests > 0 ? Math.round((acceptedRequests / totalRequests) * 100) : 0;

      //   // Fetch reviews for ratings
      //   const reviewsResponse = await apiClient.get(
      //     `${baseUrl}/review/${selectedPropertyId}`,
      //     {
      //       headers: {
      //         Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      //       },
      //     }
      //   );

      //   // Calculate average rating
      //   let averageRating = 0;
      //   const reviews = reviewsResponse.data.result.reviews || [];
      //   if (reviews.length > 0) {
      //     const totalRating = reviews.reduce((sum, review) => sum + (review.overAllRating || 0), 0);
      //     averageRating = Number((totalRating / reviews.length).toFixed(1));
      //   }

      //   // Create new analytics object without spreading response.data.result
      //   const analyticsData = {
      //     totalQrCodeScan: response.data.result.length || 0,
      //     totalServicesRequest: totalAddons,
      //     totalHouseKeepingRequest: totalHousekeeping,
      //     totalInHouseGuest: totalInHouseGuests,
      //     averageRating,
      //     serviceEfficiency,
      //     totalRequests,
      //     acceptedRequests,
      //     totalRevenue,
      //     negativeReviewsAvoided: averageRating,
      //   };

      //   console.log("Setting Analytics Data:", analyticsData);
      //   setAnalytics(analyticsData);
      // } catch (error) {
      //   console.error(error);
      //   setAnalytics(prev => ({
      //     ...prev,
      //     averageRating: 0,
      //     serviceEfficiency: 0,
      //     totalRequests: 0,
      //     acceptedRequests: 0,
      //     totalInHouseGuest: 0
      //   }));
      // } finally {
      //   setLoading(false);
      // }

      try {
        const response = await apiClient.get(
          `${baseUrl}/analytics/${selectedPropertyId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
        const data = response.data.result;
        const efficiency = ((data.totalCompletedAddOnsRequest / data.totalRequestCount) * 100).toFixed(2);
        data.serviceEfficiency = efficiency;
        
        // Cache the analytics data with timestamp
        analyticsCache[selectedPropertyId] = {
          data: data,
          timestamp: Date.now()
        };
        
        setAnalytics(data);
        console.log("Analytics Response:", response.data.result);
      } catch (error) {
        console.error(error);
        setAnalytics(prev => ({
          ...prev,
          serviceEfficiency: 0,
          totalRequestCount: 0,
          totalCompletedAddOnsRequest: 0,
          totalCompletedHouseKeepingRequest: 0,
          revenueGeneratedByRequests: 0,
          totalReviews: 0,
          guestSatisfactionScore: 0,
          totalInHouseGuests: 0
        }));
        toast.error("Error Fetching Analytics:", error);
        console.error("Error Fetching Analytics:", error);  
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [selectedPropertyId]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Function to refresh analytics data by clearing cache and fetching fresh data
  const handleRefresh = async () => {
    // Clear cache for current property
    delete analyticsCache[selectedPropertyId];
    
    // Fetch fresh data
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/analytics/${selectedPropertyId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      const data = response.data.result;
      const efficiency = ((data.totalCompletedAddOnsRequest / data.totalRequestCount) * 100).toFixed(2);
      data.serviceEfficiency = efficiency;
      
      // Cache the fresh analytics data with timestamp
      analyticsCache[selectedPropertyId] = {
        data: data,
        timestamp: Date.now()
      };
      
      setAnalytics(data);
      toast.success("Analytics data refreshed successfully");
      console.log("Analytics Response:", response.data.result);
    } catch (error) {
      console.error(error);
      setAnalytics(prev => ({
        ...prev,
        serviceEfficiency: 0,
        totalRequestCount: 0,
        totalCompletedAddOnsRequest: 0,
        totalCompletedHouseKeepingRequest: 0,
        revenueGeneratedByRequests: 0,
        totalReviews: 0,
        guestSatisfactionScore: 0,
        totalInHouseGuests: 0
      }));
      toast.error("Error refreshing analytics data");
      console.error("Error Fetching Analytics:", error);  
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="sm:flex sm:items-center mt-6 animate-fadeIn">
          <div className="sm:flex-auto">
            <h1 className="text-2xl text-gray-800 tracking-tight">Analytics Dashboard</h1>
            <p className="mt-2 text-sm text-gray-600 leading-6">
              A comprehensive overview of your property's performance and guest activities.
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
              title="Refresh analytics data"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>
        </div>

        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Summary Section */}
            <div className="mt-8 relative overflow-hidden bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-lg shadow-lg p-6 text-white animate-slideIn">
              <div className="absolute inset-0 bg-gradient-to-r from-white/[0.07] to-transparent opacity-20"></div>
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600/30 to-indigo-500/30 blur-3xl"></div>
              <div className="relative">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center transform hover:scale-105 transition-transform">
                    <p className="text-base text-indigo-100">Service Efficiency</p>
                    <p className="text-3xl mt-1 animate-numberRise">{analytics.serviceEfficiency}%</p>
                    <p className="text-sm text-indigo-200 mt-1">
                      {analytics.totalCompletedAddOnsRequest} of {analytics.totalRequestCount} requests accepted
                    </p>
                  </div>
                  <div className="text-center border-l border-r border-indigo-400 transform hover:scale-105 transition-transform">
                    <p className="text-base text-indigo-100">Total Add-Ons Revenue</p>
                    <p className="text-3xl mt-1 animate-numberRise">{formatCurrency(analytics.revenueGeneratedByRequests)}</p>
                    <p className="text-sm text-indigo-200 mt-1">
                      Total revenue from add-ons
                    </p>
                  </div>
                  <div className="text-center transform hover:scale-105 transition-transform">
                    <p className="text-base text-indigo-100">Negative Reviews Avoided</p>
                    <p className="text-3xl mt-1 animate-numberRise">{analytics.totalReviews}</p>
                    <p className="text-sm text-indigo-200 mt-1">
                      Total negative reviews avoided to google reviews
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <hr className="border-t border-gray-200 my-8 animate-fadeIn" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-cardsAppear">
              <Card
                title="QR Code Scans"
                count={analytics.totalQrCodeScan}
                link="/dashboard/analytics/details?type=qrCode"
                icon={<Scan className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="Service Requests"
                count={analytics.totalRequestCount}
                link="/dashboard/analytics/details?type=service"
                icon={<Bell className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="Housekeeping Requests"
                count={analytics.totalHouseKeepingRequest}
                link="/dashboard/analytics/details?type=houseKeeping"
                icon={<Home className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="In-house Guests"
                count={analytics.totalInHouseGuests}
                link="/dashboard/analytics/details?type=currentInHouseGuests"
                icon={<Users className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="Service Efficiency"
                count={`${analytics.serviceEfficiency}%`}
                subtitle={`${analytics.totalCompletedAddOnsRequest} of ${analytics.totalRequestCount} requests accepted`}
                link="/dashboard/analytics/details?type=efficiency"
                icon={<CheckCircle className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="Guest Satisfaction"
                count={`${analytics.guestSatisfactionScore}/5`}
                link="/dashboard/analytics/details?type=ratings"
                icon={<Star className="w-8 h-8 text-indigo-600" />}
              />
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes numberRise {
          from {
            transform: translateY(10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes cardsAppear {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: .5; }
          100% { opacity: 1; }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }

        .animate-slideIn {
          animation: slideIn 0.7s ease-out;
        }

        .animate-numberRise {
          animation: numberRise 1s ease-out;
        }

        .animate-cardsAppear {
          animation: cardsAppear 0.8s ease-out;
        }

        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default Overview;
